/**
 * humanSupport.js — adapter from existing learning events to the Human Support Escalation engine (/api/human-support).
 * Reports attempts/support/prerequisite checks for the SAME objective; the server owns the state machine and audit.
 * Nothing here is child-facing; the only surface is the Parent Dashboard card. Never blocks learning.
 */
import { getChildId, learnerHeaders } from "@/lib/childId";
import { backendTier } from "@/lib/readiness";
import { objectivesFor } from "@/lib/learningGraph";
import { getOutcomeMastery } from "@/lib/mastery";

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_TIER = { little: "seedling", big: "explorer", older: "pioneer" };
const tier = () => { try { return DEVICE_TIER[localStorage.getItem("we_age_group")] || "explorer"; } catch { return "explorer"; } };

function post(body) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return Promise.resolve(null);
  return fetch(`${API}/api/human-support/events`, { method: "POST", headers: learnerHeaders(), body: JSON.stringify({ learnerId: getChildId(), ageTier: tier(), ...body }) })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null);
}

/** Prerequisite objectives (from the learning graph) that are not yet mastered on this device. */
export function prerequisiteGaps(objectiveId) {
  try {
    const prereqs = objectivesFor(objectiveId).flatMap((o) => o.prerequisites || []);
    return [...new Set(prereqs)].filter((p) => !getOutcomeMastery(p).mastered);
  } catch { return []; }
}

/** Practice / Check finished. Low independent scores also trigger the prerequisite check automatically. */
export async function reportAttempt({ objectiveId, score, total, subject = "", label = "", assisted = false, evidenceId = null }) {
  if (!objectiveId || !total) return null;
  const res = await post({ type: "attempt", objectiveId, subject, objectiveLabel: label, score, total, independent: !assisted, assistanceLevel: assisted ? "hint" : null, evidenceId });
  if (res && score / total < 0.6 && ["ADAPTIVE_SUPPORT", "PREREQUISITE_SUPPORT"].includes(res.state)) {
    await post({ type: "prerequisite_check", objectiveId, subject, prerequisiteGaps: prerequisiteGaps(objectiveId) });
  }
  return res;
}

/** Re-reading the lesson / a worked example after difficulty is an automated repair — record it (only when difficulty exists). */
export function reportSupport(objectiveId, supportType = "alternate_explanation", subject = "") {
  if (!objectiveId) return Promise.resolve(null);
  const attempts = getOutcomeMastery(objectiveId).attempts || [];
  if (!attempts.some((a) => a.total && a.score / a.total < 0.6)) return Promise.resolve(null);
  return post({ type: "support", objectiveId, supportType, subject });
}

export const reportAssistance = (objectiveId, level = "hint") => post({ type: "assistance", objectiveId, assistanceLevel: level });
export const reportPrerequisiteReviewed = (objectiveId) => post({ type: "prerequisite_check", objectiveId, supportType: "prerequisite_review" });
export const reportChoice = (objectiveId, choice) => post({ type: "choice", objectiveId, choice });
export const fetchLearnerState = (learnerId = getChildId()) => fetch(`${API}/api/human-support/learner/${encodeURIComponent(learnerId)}/state`, { headers: learnerHeaders() }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
